import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getReportSelectors } from '../../report-common';
//////////////////////////////////////////////////

const {
  makeSelectUsedReport,
  makeSelectPagination,
  makeSelectTotalCount,
  makeSelectListLoading,
  makeSelectPageVisited,
  makeSelectReportStatus,
  store: selectCarrierStore,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
} = getReportSelectors('carrier');

const selectRoleStore = (state) => state.roles;

const makeSelectItemList = () => createSelector(
  selectCarrierStore,
  (state) => R.sortBy(
    R.prop('index'),
    R.values(state.itemList),
  ),
);

const makeSelectRoleOptions = () => createSelector(
  selectRoleStore,
  (role) => R.compose(
    R.map(({ name, guid }) => ({ label: name, value: guid })),
    R.values,
  )(role.roleList),
);

const makeSelectCarrierActiveTab = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.activeTab,
);

const makeSelectCarrierConfigs = () => createSelector(
  selectCarrierStore,
  (carrier) => ({
    emailGroup: G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      carrier.carrierConfigs,
      GC.CARRIER_EMAIL_GROUP,
    ),
    defaultBranchCurrency: G.getConfigValueFromStore(
      GC.GENERAL_BRANCH_DEFAULT_CURRENCY,
      carrier.generalConfigs,
    ),
    insuranceType: G.mapDropdownConfigWithParentGuid(
      carrier.carrierConfigs, GC.CARRIER_INSURANCE_TYPE,
    ),
    documentType: G.mapDropdownConfigWithParentGuid(
      carrier.carrierConfigs, GC.CARRIER_DOCUMENT_TYPE,
    ),
    deactivateType: G.mapDropdownConfigWithParentGuid(
      carrier.carrierConfigs, GC.CARRIER_DEACTIVATED_TYPE,
    ),
    transportationMode: G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      carrier.generalConfigs, GC.GENERAL_MODE_TRANSPORTATION,
    ),
    serviceType: G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      carrier.generalConfigs, GC.GENERAL_TRANSPORTATION_SERVICE_TYPE,
    ),
    equipmentType: G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      carrier.generalConfigs, GC.GENERAL_EQUIPMENTS,
    ),
    locationType: G.mapDropdownConfigWithParentGuid(
      carrier.templateConfigs, GC.TEMPLATES_LOCATION_TYPE,
    ),
    carrierSequences: G.addEmptyOptionToDropDown(R.map((item) => ({
      value: item.guid,
      label: item.displayName,
    }), carrier.carrierSequences)),
    carrierReferenceTypes: G.addEmptyOptionToDropDown(R.map((item) => ({
      label: item.name,
      value: item.guid,
    }), carrier.carrierReferenceTypes)),
  }),
);

const makeSelectCollapsedGroup = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.collapsedGroup,
);

const makeSelectCarrierBranchGuid = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.carrierDetails[GC.FIELD_BRANCH_GUID],
);

const makeSelectCarrierDetails = () => createSelector(
  selectCarrierStore,
  (carrier) => ({
    ...carrier.carrierDetails,
    terminal: carrier.terminalDetails,
    carrierFinance: carrier.carrierFinance,
    terminalFinance: carrier.terminalFinance,
  }),
);

const makeSelectCarrierGuid = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.carrierDetails.guid,
);

const makeSelectDispatchConfigs = () => createSelector(
  selectCarrierStore,
  ({ dispatchConfigs }) => dispatchConfigs,
);

const makeSelectCarrierRefList = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier[GC.UI_FIELD_REFERENCES],
);

const makeSelectCarrierContracts = () => createSelector(
  selectCarrierStore,
  ({ carrierContracts }) => carrierContracts,
);

const makeSelectCarrierContractAwardedLines = () => createSelector(
  selectCarrierStore,
  ({ carrierContractAwardedLines }) => carrierContractAwardedLines,
);

const makeSelectCustomerContractAwardedLines = () => createSelector(
  selectCarrierStore,
  ({ customerContractAwardedLines }) => customerContractAwardedLines,
);

const makeSelectCarrierContract = () => createSelector(
  selectCarrierStore,
  ({ carrierContract }) => carrierContract,
);

const makeSelectCustomersContract = () => createSelector(
  selectCarrierStore,
  ({ customersContract }) => customersContract,
);

const makeSelectCustomerContract = () => createSelector(
  selectCarrierStore,
  ({ customerContract }) => customerContract,
);

const makeSelectCarrierInsurances = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.insurances,
);

const makeSelectCarrierAssocCompanies = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.assocCompanies,
);

const makeSelectCarrierDocs = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.documents,
);

const makeSelectCarrierTerminals = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.terminals,
);

const makeSelectTerminalContacts = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.terminalContacts,
);

const makeSelectCarrierCertificates = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.certificates,
);

const makeSelectTerminalFinance = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.terminalFinance,
);

const makeSelectTerminalPayLocations = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.terminalPayLocations,
);

const makeSelectCarrierFinance = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.carrierFinance,
);

const makeSelectCarrierPayLocations = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.carrierPayLocations,
);

const makeSelectCarrierAssessorials = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.carrierAssessorials,
);

const makeSelectContractVariableValues = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.variableValues,
);

const makeSelectCarrierContractAssessorials = () => createSelector(
  selectCarrierStore,
  (carrier) => carrier.carrierContractAssessorials,
);

const makeSelectCarrierPortalLoginUsers = () => createSelector(
  selectCarrierStore,
  ({ carrierPortalLoginUsers }) => carrierPortalLoginUsers,
);


const makeSelectMailList = () => createSelector(
  selectCarrierStore,
  ({ mailList }) => mailList,
);

const makeSelectOnboardingPackage = () => createSelector(
  selectCarrierStore,
  ({ sendPackage }) => sendPackage,
);

const makeSelectCustomerContractAssessorials = () => createSelector(
  selectCarrierStore,
  ({ customerContractAssessorials }) => customerContractAssessorials,
);

const makeSelectSafety = () => createSelector(
  selectCarrierStore,
  ({ safety }) => safety,
);

const makeSelectCarrierDetailsUSDotNumber = () => createSelector(
  selectCarrierStore,
  ({ carrierDetails }) => R.prop(GC.FIELD_CARRIER_USDOT_NUMBER, carrierDetails),
);

const makeSelectCarrierContractRate = () => createSelector(
  selectCarrierStore,
  ({ carrierContractRate }) => carrierContractRate,
);

const makeSelectActivePage = () => createSelector(
  selectCarrierStore,
  ({ activePage }) => activePage,
);

const makeSelectAdditionalContactList = () => createSelector(
  selectCarrierStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['additionalContactList']),
  ),
);

const makeSelectIntegrationInfoList = () => createSelector(
  selectCarrierStore,
  ({ integrationInfoList }) => integrationInfoList,
);

const makeSelectIntegrationAuditList = () => createSelector(
  selectCarrierStore,
  ({ integrationAuditList }) => integrationAuditList,
);

const makeSelectCarrierIntegrationOptions = () => createSelector(
  selectCarrierStore,
  ({ carrierIntegrationOptions }) => carrierIntegrationOptions,
);

const makeSelectAccessorialConfigParams = () => createSelector(
  selectCarrierStore,
  R.pick(['accessorialConfigList', 'accessorialConfigOptions']),
);

const makeSelectContractBillTo = () => createSelector(
  selectCarrierStore,
  ({ contractBillTo }) => contractBillTo,
);

const makeSelectLoadBoardIntegrationList = () => createSelector(
  selectCarrierStore,
  ({ loadBoardIntegrationList }) => loadBoardIntegrationList,
);

const makeSelectLoadBoardSynchronizationParams = () => createSelector(
  selectCarrierStore,
  ({ parentBranchGuids, loadBoardIntegrationList }) => {
    if (G.isNilOrEmpty(loadBoardIntegrationList)) {
      return {
        [GC.FIELD_CARRIER_ADDED_TO_NETWORK]: false,
        [GC.FIELD_CARRIER_ALLOWED_BOOK_IT_NOW]: false,
      };
    }

    const indexed = R.indexBy(R.prop(GC.BRANCH_GUID), loadBoardIntegrationList);

    const mapped = R.compose(
      R.map((item) => G.getPropFromObject(item, indexed)),
      R.filter((item) => R.has(item, indexed)),
    )(parentBranchGuids);

    const addedToNetwork = R.pathOr(false, [0, GC.FIELD_CARRIER_ADDED_TO_NETWORK], mapped);
    const allowedBookItNow = R.pathOr(false, [0, GC.FIELD_CARRIER_ALLOWED_BOOK_IT_NOW], mapped);

    return { addedToNetwork, allowedBookItNow };
  },
);

const makeSelectAvailableForCarrierLoadBoards = () => createSelector(
  selectCarrierStore,
  ({ availableForCarrierLoadBoards }) => availableForCarrierLoadBoards,
);

const makeSelectCarrierPaymentTerms = () => createSelector(
  selectCarrierStore,
  ({ carrierPaymentTerms }) => carrierPaymentTerms,
);

const makeSelectCarrierInternalDispatchSettings = () => createSelector(
  selectCarrierStore,
  ({ carrierInternalDispatchSettings }) => carrierInternalDispatchSettings,
);

const makeSelectShowTrackingSynchronization = () => createSelector(
  selectCarrierStore,
  ({ showTrackingSynchronization }) => showTrackingSynchronization,
);

export {
  makeSelectSafety,
  makeSelectItemList,
  selectCarrierStore,
  makeSelectMailList,
  makeSelectUsedReport,
  makeSelectPagination,
  makeSelectTotalCount,
  makeSelectActivePage,
  makeSelectListLoading,
  makeSelectRoleOptions,
  makeSelectCarrierGuid,
  makeSelectCarrierDocs,
  makeSelectPageVisited,
  makeSelectReportStatus,
  makeSelectCarrierFinance,
  makeSelectContractBillTo,
  makeSelectCarrierRefList,
  makeSelectCarrierConfigs,
  makeSelectCollapsedGroup,
  makeSelectCarrierDetails,
  makeSelectTitleSortValues,
  makeSelectDispatchConfigs,
  makeSelectTerminalFinance,
  makeSelectCarrierContract,
  makeSelectCustomerContract,
  makeSelectCarrierTerminals,
  makeSelectTerminalContacts,
  makeSelectCarrierActiveTab,
  makeSelectAvailableReports,
  makeSelectCarrierContracts,
  makeSelectTableTitleFilters,
  makeSelectCarrierBranchGuid,
  makeSelectCarrierInsurances,
  makeSelectCustomersContract,
  makeSelectOnboardingPackage,
  makeSelectCarrierContractRate,
  makeSelectIntegrationInfoList,
  makeSelectCarrierPayLocations,
  makeSelectCarrierCertificates,
  makeSelectCarrierAssessorials,
  makeSelectCarrierPaymentTerms,
  makeSelectTerminalPayLocations,
  makeSelectIntegrationAuditList,
  makeSelectAdditionalContactList,
  makeSelectCarrierAssocCompanies,
  makeSelectContractVariableValues,
  makeSelectCarrierPortalLoginUsers,
  makeSelectAccessorialConfigParams,
  makeSelectLoadBoardIntegrationList,
  makeSelectCarrierDetailsUSDotNumber,
  makeSelectCarrierIntegrationOptions,
  makeSelectCarrierContractAssessorials,
  makeSelectShowTrackingSynchronization,
  makeSelectCarrierContractAwardedLines,
  makeSelectCustomerContractAwardedLines,
  makeSelectCustomerContractAssessorials,
  makeSelectAvailableForCarrierLoadBoards,
  makeSelectLoadBoardSynchronizationParams,
  makeSelectCarrierInternalDispatchSettings,
};
